import './TagsGroup.css';
import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { Tag } from './Tag';
import classnames from 'classnames';
import { Icon } from '../Icon/Icon';
import { Props as TagProps } from './Tag';
import { Tooltip } from '../Tooltip/Tooltip';
import * as PopperJS from 'popper.js';
import { Spinner } from '../Spinner/Spinner';

interface Tag extends TagProps {
  id: string;
  label?: string | null;
}

interface Props {
  tags?: Tag[];
  onRemove?: (id: number) => void;
  onClear?: () => void;
  expandHiddenTags?: PopperJS.Placement;
  input?: JSX.Element;
  actions?: JSX.Element;
  truncateAt?: number;
  disableTagRemove?: boolean;
  loading?: boolean;
}

export function TagsGroup({
  tags = [],
  onRemove,
  onClear,
  input,
  actions,
  expandHiddenTags,
  disableTagRemove,
  loading,
}: Props): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const [overflowIndex, setOverflowIndex] = useState<number | null>(null);
  const [containerRefWidth, setContainerRefWidth] = useState(0);

  const tagElements = containerRef.current?.children;

  if (containerRef.current && containerRef.current.scrollLeft) {
    containerRef.current.scrollLeft = 0;
  }

  useEffect(() => {
    const firstTag = tagElements?.[0]?.clientWidth || 0;
    let tagsWidthSum = input ? 60 : 0;

    if (firstTag + tagsWidthSum > containerRefWidth) {
      return setOverflowIndex(1);
    }

    for (let i = 0; i < (tagElements?.length || 0); i += 1) {
      const tag = tagElements?.item(i);

      tagsWidthSum += tag?.clientWidth || 0;

      if (tagsWidthSum > containerRefWidth) {
        if (overflowIndex !== i) {
          setOverflowIndex(i);
        }
        break;
      }
    }

    if (tagsWidthSum < containerRefWidth && overflowIndex !== null) {
      setOverflowIndex(null);
    }
  }, [tagElements?.length]);

  useLayoutEffect(() => {
    setContainerRefWidth(containerRef.current?.clientWidth || 0);
  });

  if (loading) {
    return <Spinner color="var(--color-text-label)" scale=".84" />;
  }

  function handleOnRemove(index: number): void {
    if (onRemove) {
      onRemove(index);
    }
  }

  function renderHiddenTagsCount(): JSX.Element {
    const hiddenTagsCount = (
      <span className="tags-group__more-number">+{(tags.length - (overflowIndex || 0)).toString()}</span>
    );

    if (overflowIndex && expandHiddenTags) {
      return (
        <Tooltip
          mode="click"
          position={expandHiddenTags}
          arrow={false}
          rounded={false}
          reference={<>{hiddenTagsCount}</>}
        >
          <div className="tags-group__hidden-tags">
            {tags.slice(overflowIndex).map((tag, index) => {
              return (
                <div key={tag.id} className="tags-group__hidden-tag">
                  <Tag
                    className="tags-group__tag"
                    text={tag.label || tag.id}
                    backgroundColor={tag.backgroundColor}
                    accessory={
                      <Icon
                        onClick={() => handleOnRemove(overflowIndex + index)}
                        className="tags-group__tag-icon"
                        name="remove"
                      />
                    }
                  />
                </div>
              );
            })}
          </div>
        </Tooltip>
      );
    }
    return hiddenTagsCount;
  }

  return (
    <div className="tags-group">
      <div className="tags-group__container" ref={containerRef}>
        {tags.map((tag, index) => (
          <Tag
            className={classnames('tags-group__tag', {
              'tags-group__tag--hidden': overflowIndex ? index >= overflowIndex : false,
            })}
            key={tag.id}
            text={tag.label || tag.id}
            backgroundColor={tag.backgroundColor}
            accessory={
              !disableTagRemove &&
              onRemove && <Icon onClick={() => handleOnRemove(index)} className="tags-group__tag-icon" name="remove" />
            }
          />
        ))}
        {input && <div className="tags-group__input">{input}</div>}
      </div>
      <div
        className={classnames('tags-group__clear', {
          'tags-group__clear--active': tags.length > 1,
        })}
      >
        <Icon name="remove" onClick={onClear} />
      </div>
      <div
        className={classnames('tags-group__more', {
          'tags-group__more--active': overflowIndex && tags.length - overflowIndex > 0,
        })}
      >
        {renderHiddenTagsCount()}
        {actions}
      </div>
    </div>
  );
}
